import moment from 'moment'

import { formatService, FORMAT_TYPES as COMMON_FORMAT_TYPES } from '../../common/format.service'

const MISSING_POINT_DISPLAY_OPTIONS = {
  PREVIOUS: 'previous',
  NEXT: 'next'
}

const FORMAT_TYPES = {
  ...COMMON_FORMAT_TYPES,
  DATE: 'date',
  RELATIVE_DATE: 'relative_date'
}

function getFormatter ({
  formatType,
  timeFormat = 'YYYY-MM-DD',
  relativeTimeUnit,
  relativeTimeIndexDate
}) {
  switch (formatType) {
    case FORMAT_TYPES.DATE:
      return (value) => moment.utc(value).format(timeFormat)
    case FORMAT_TYPES.RELATIVE_DATE:
      return (value) => moment.utc(value).diff(moment.utc(relativeTimeIndexDate), relativeTimeUnit)
    case FORMAT_TYPES.NUMBER:
      return (value) => formatService.formatNumber(value)
    case FORMAT_TYPES.NUMBER_SHORT:
      return (value) => formatService.formatNumberShort(value)
    case FORMAT_TYPES.NUMBER_SHORTER:
      return (value) => formatService.formatNumberShorter(value)
    case FORMAT_TYPES.PERCENT:
      return (value) => formatService.formatPercent(value)
    case FORMAT_TYPES.PERCENT_SHORT:
      return (value) => formatService.formatPercentShort(value)
    case FORMAT_TYPES.PERCENT_SHORTER:
      return (value) => formatService.formatPercentShorter(value)
    default:
      return (value) => value
  }
}

export function formatValue (value, config) {
  const formatter = getFormatter(config)
  if (formatter) {
    return formatter(value)
  } else if (value === 0 || (value && value.toString)) {
    return value.toString()
  } else {
    return ''
  }
}

export function getAxesFormatters ({ isTimeSeries, timeFormat, relativeTimeUnit, relativeTimeIndexDate, xFormat, yFormat }) {
  let xFormatType = xFormat || FORMAT_TYPES.NUMBER
  if (isTimeSeries) {
    xFormatType = relativeTimeUnit ? FORMAT_TYPES.RELATIVE_DATE : FORMAT_TYPES.DATE
  }
  // Replace number and percent with short versions
  yFormat = (yFormat === FORMAT_TYPES.NUMBER) ? FORMAT_TYPES.NUMBER_SHORT : yFormat
  yFormat = (yFormat === FORMAT_TYPES.PERCENT) ? FORMAT_TYPES.PERCENT_SHORT : yFormat
  const x = getFormatter({ formatType: xFormatType, timeFormat, relativeTimeUnit, relativeTimeIndexDate })
  const y = getFormatter({ formatType: yFormat })

  return {
    x,
    xWithUnit: (value) => `${x(value)} ${relativeTimeUnit || ''}`.trim(),
    y
  }
}

export function formatDataForTooltip ({ closestPoints, series, primaryValueFormat, secondaryValueFormat, ciFormat, seriesToHide, missingPointDisplay }) {
  return closestPoints
    .map(({ current: closestPoint, previous, next }, i) => {
      if (!closestPoint) {
        // If missing, see if we want to use previous or next value instead
        switch (missingPointDisplay) {
          case MISSING_POINT_DISPLAY_OPTIONS.PREVIOUS:
            closestPoint = previous
            break
          case MISSING_POINT_DISPLAY_OPTIONS.NEXT:
            closestPoint = next
            break
        }
        // If still missing, no more work to do
        if (!closestPoint) {
          return {}
        }
      }

      const { id, name, label, color, bands } = series[i]
      const result = {
        id,
        label: label || name,
        color,
        primaryValue: formatValue(closestPoint.y, { formatType: primaryValueFormat }),
        secondaryValue: closestPoint.y2 && formatValue(closestPoint.y2, { formatType: secondaryValueFormat }),
        rawPrimaryValue: closestPoint.y,
        bands: bands.map(({ name, points, color }) => {
          const point = points.find(({ x }) => x === closestPoint.x)
          return {
            label: name,
            color,
            value: `${formatValue(point.y0, { formatType: ciFormat })} - ${formatValue(point.y1, { formatType: ciFormat })}`
          }
        }).reverse()
      }

      return result
    })
    .filter(({ label, rawPrimaryValue }) => ((rawPrimaryValue !== undefined) && (!seriesToHide[label])))
    .sort((a, b) => b.rawPrimaryValue - a.rawPrimaryValue)
}
